/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { useTheme, Box } from '@staffino/primitive-controls';
import YesNoButton from '../components/yes-no-button';

type Props = {
  name: string;
  backgroundColor?: string;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  lowerBoundScore?: number;
  upperBoundScore?: number;
  reverseButtonOrder?: boolean;
  value?: number;
  onChange?: (score: number) => void;
} & React.ComponentProps<typeof Box>;

const YesNoControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<unknown>
> = React.forwardRef(
  (
    {
      name,
      backgroundColor,
      lowerBoundLabel,
      upperBoundLabel,
      lowerBoundScore,
      upperBoundScore,
      reverseButtonOrder,
      value,
      onChange,
      ...rest
    }: Props,
    ref: React.Ref<unknown>,
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const selectedScore = parseInt(value?.toString(), 10);
    const validBackgroundColor = tinycolor(backgroundColor).isValid()
      ? backgroundColor
      : theme.colors.primaryBackground;

    const handleChange = useCallback(
      (score) => onChange?.(parseInt(score, 10)),
      [onChange],
    );

    return (
      <Box
        {...rest}
        flexDirection={'row' as const}
        justifyContent="space-between"
      >
        {!reverseButtonOrder && (
          <YesNoButton
            innerRef={ref}
            name={name}
            score={upperBoundScore ?? 1}
            label={upperBoundLabel || t('Yes')}
            color={
              selectedScore === undefined
                ? 'transparent'
                : selectedScore === (upperBoundScore ?? 1)
                  ? validBackgroundColor
                  : 'transparent'
            }
            hoverColor={
              selectedScore === undefined
                ? theme.colors.separator
                : selectedScore === (upperBoundScore ?? 1)
                  ? validBackgroundColor
                  : theme.colors.separator
            }
            hoverFontColor={selectedScore === undefined ? '#586474' : '#ffffff'}
            isSelected={selectedScore === (upperBoundScore ?? 1)}
            onChange={handleChange}
          />
        )}
        <YesNoButton
          innerRef={ref}
          name={name}
          score={lowerBoundScore ?? 0}
          label={lowerBoundLabel || t('No')}
          color={
            selectedScore === undefined
              ? 'transparent'
              : selectedScore === (lowerBoundScore ?? 0)
                ? validBackgroundColor
                : 'transparent'
          }
          hoverColor={
            selectedScore === undefined
              ? theme.colors.separator
              : selectedScore === (lowerBoundScore ?? 0)
                ? validBackgroundColor
                : theme.colors.separator
          }
          hoverFontColor={selectedScore === undefined ? '#586474' : '#ffffff'}
          isSelected={selectedScore === (lowerBoundScore ?? 1)}
          onChange={handleChange}
        />
        {reverseButtonOrder && (
          <YesNoButton
            innerRef={ref}
            name={name}
            score={upperBoundScore ?? 1}
            label={upperBoundLabel || t('Yes')}
            color={
              selectedScore === undefined
                ? 'transparent'
                : selectedScore === (upperBoundScore ?? 1)
                  ? validBackgroundColor
                  : 'transparent'
            }
            hoverColor={
              selectedScore === undefined
                ? theme.colors.separator
                : selectedScore === (upperBoundScore ?? 1)
                  ? validBackgroundColor
                  : theme.colors.separator
            }
            hoverFontColor={selectedScore === undefined ? '#586474' : '#ffffff'}
            isSelected={selectedScore === (upperBoundScore ?? 1)}
            onChange={handleChange}
          />
        )}
      </Box>
    );
  },
);

export default YesNoControl;
